import { createFileRoute } from "@tanstack/react-router";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { useAppSelector } from "../../../../redux/hooks";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { createColumnBuilder } from "../../../../components/table/columnBuilder";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../../../../components/table/utils";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { useQuery } from "@tanstack/react-query";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { type OrgQueryV4Row } from "../../../../modules/perf_reports/api/organization/v4OrgReportByOrg";
import { useGetV4OrgReportByDate } from "../../../../modules/perf_reports/api/organization/v4OrgReportByDate";
import { useGetV4OrgReportTotals } from "../../../../modules/perf_reports/api/organization/v4OrgReportTotals";
import AgencyDashboardTable, {
  AGENCY_DASHBOARD_STAT_CHANNELS,
} from "../../../../modules/organization/AgencyDashboardTable";
import MarketplaceStatsFilter from "../../../../modules/merchandising/components/MarketplaceStatsFilter";
import { Accordion } from "../../../../components/core/basic/Accordion";
import { useMarketplaceLabel } from "../../../../utils/marketplaceConstants";
import { getOrganizationsOptions } from "../../../../modules/admin/api/useOrganizations";
import { OrgCell } from "../../../../modules/organization/OrgCell";
import { HStack } from "../../../../../styled-system/jsx";
import { DownloadV4Report } from "../../../../modules/perf_reports/DownloadReport";
import { useV4OrgReportExport } from "../../../../modules/perf_reports/api/organization/v4OrgReportExport";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";

const GOAL_METRIC_IDS = [
  "daily_sales_goal",
  "total_sales_pacing_percent",
  "total_sales_pacing_offset",
  "daily_ad_spend_goal",
  "ad_spend_pacing_percent",
  "ad_spend_pacing_offset",
  "total_acos_goal",
  "tacos_pacing_percent",
  "tacos_pacing_offset",
];

const columnHelper = createColumnBuilder<OrgQueryV4Row>();

const OrgReportBody = () => {
  const { t } = useTranslation(["admin"]);
  const getMarketplaceLabel = useMarketplaceLabel();
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const [selectedMarketplace, setSelectedMarketplace] = useState(marketPlace);
  const [showHiddenOrgs, setShowHiddenOrgs] = useState(false);

  const trellisProfilesQuery = useQuery(getOrganizationsOptions());

  const acosOrRoas = useAcosOrRoas();
  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");

  const marketPlaceOptions = useMemo(() => {
    return [...new Set(trellisProfilesQuery.data?.flatMap((profile) => profile.marketplaces))];
  }, [trellisProfilesQuery.data]);

  const standardMetricIds = useStandardEntityMetrics("organization");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const goalMetricsEnabled = user.is_staff;

  const tableMetricIds = useMemo(
    () => (goalMetricsEnabled ? [...standardMetricIds, ...GOAL_METRIC_IDS] : standardMetricIds),
    [standardMetricIds, goalMetricsEnabled],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: t(($) => $.admin["Organization"]),
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: (props) => {
          return <OrgCell orgData={props.row.original} marketPlace={selectedMarketplace} />;
        },
      }),
      tagsEnhancedEnabled ?
        columnHelper.accessor("tags_enhanced", {
          id: "tags_enhanced.id",
          header: t(($) => $.admin["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
              label: t(($) => $.admin["Tags"]),
            },
          },
        })
      : columnHelper.accessor((row) => row, {
          id: "tags",
          header: t(($) => $.admin["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
            },
          },
        }),
      ...tableMetricIds.map((metricId) =>
        columnHelper.metric(metricId, { formatOptions: { marketPlace: selectedMarketplace } }),
      ),
    ],
    [tableMetricIds, selectedMarketplace, tagsEnhancedEnabled, t],
  );
  const defaultSelectedColumns = [
    "name",
    "sales",
    "units",
    acosOrRoas("acos", "roas"),
    "margin",
    "ad_sales",
    "ad_spend",
    acosOrRoas("roas", "acos"),
    "roms",
    "cost",
    "promo_cost",
    "clicks",
    "impressions",
    "ctr",
    "orders",
    "conversion_rate",
    "total_acos",
  ];

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      tags: false,
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["name"],
      end: [],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `agencyDashboardOrgReport`,
    initialState: initialStateConfig,
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);

  const filters = useMemo(() => {
    const hiddenOrgsFilter =
      showHiddenOrgs ? [] : [{ id: "org_is_visible", value: { op: "=", value: !showHiddenOrgs } }];
    const selectedIdsFilter = selectedIds.length ? [{ id: "org_id", value: { op: "anyof", value: selectedIds } }] : [];
    const orgChannelFilter = [
      {
        id: "org_type",
        value: {
          op: "anyof",
          value: AGENCY_DASHBOARD_STAT_CHANNELS,
        },
      },
    ];

    return [...orgChannelFilter, ...state.columnFilters, ...hiddenOrgsFilter, ...selectedIdsFilter];
  }, [state.columnFilters, showHiddenOrgs, selectedIds]);

  const byDateOptionsGetter = useGetV4OrgReportByDate(selectedMarketplace);
  const orgsByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters,
      search: debounceSearch,
      allActive: false,
    }),
  );
  const totalOptionsGetter = useGetV4OrgReportTotals(selectedMarketplace);
  const orgTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters,
      search: debounceSearch,
      allActive: false,
    }),
  );

  const metrics = useMemo(() => {
    return standardMetricIds.map((metricId) => getMetricSpec(metricId));
  }, [standardMetricIds]);
  const exportMutation = useV4OrgReportExport(filters, selectedMarketplace);

  return (
    <PageContainer withBreadcrumb={true}>
      <MetricsCard
        id={"agencyDashboardOrgReport"}
        title={t(($) => $.admin["Performance Overview"])}
        totals={{
          data: orgTotalsReportQuery.data?.data,
          loading:
            orgTotalsReportQuery.isFetching && (!orgTotalsReportQuery.data || orgTotalsReportQuery.isPlaceholderData),
        }}
        timeline={{
          data: orgsByDateReportQuery.data?.data ?? [],
          loading:
            orgsByDateReportQuery.isFetching &&
            (!orgsByDateReportQuery.data || orgsByDateReportQuery.isPlaceholderData),
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["sales", "ad_sales", "ad_spend"]}
        group={group}
        // Hiding annotations since they only get fetch for current profile
        showAnnotations={false}
        byline={
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
          </>
        }
        context={"target"}
        inlineStatus={
          <MarketplaceStatsFilter
            selectedMarketplace={selectedMarketplace}
            setSelectedMarketplace={setSelectedMarketplace}
            allowedMarketplaces={marketPlaceOptions}
          />
        }
        marketPlace={selectedMarketplace}
      >
        <Accordion
          title={t(($) => $.admin["Brands in {marketplace}"], {
            marketplace: getMarketplaceLabel(selectedMarketplace),
          })}
          defaultExpanded={true}
        >
          <AgencyDashboardTable
            state={state}
            initialState={initialState}
            setters={setters}
            key={"organization"}
            selectedMarketplace={selectedMarketplace}
            showHiddenOrgs={showHiddenOrgs}
            setShowHiddenOrgs={setShowHiddenOrgs}
            columns={columns}
          />
          <HStack width={"100%"} padding={"small"}>
            <DownloadV4Report downloadMutation={exportMutation} />
          </HStack>
        </Accordion>
      </MetricsCard>
    </PageContainer>
  );
};

const AgencyOrgReport = () => {
  const { t } = useTranslation(["admin"]);
  const breadCrumbItems = [
    {
      name: t(($) => $.admin["Agency Dashboard"]),
      href: "/user/agency/dashboard",
    },
  ];

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <OrgReportBody />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/agency/dashboard")({
  component: AgencyOrgReport,
  staticData: { application: "Merchandising" },
});
